import React from 'react';
import { Box, Typography, Paper, Grid, Divider } from '@mui/material';
import { QualityMetrics } from './QualityMetrics';

interface ColabfoldResultsProps {
  results: Record<string, unknown>;
  qualityMetrics?: Record<string, unknown>;
}

/**
 * Component to display ColabFold results
 */
export const ColabfoldResults: React.FC<ColabfoldResultsProps> = ({ 
  results,
  qualityMetrics
}) => {
  // Get pLDDT scores from either quality metrics or results
  let plddtScores: number[] = [];
  
  // First try to get from qualityMetrics
  if (qualityMetrics && typeof qualityMetrics === 'object') {
    const scores = qualityMetrics.scores as Record<string, unknown> | undefined;
    if (scores && 'plddt' in scores) {
      plddtScores = scores.plddt as number[];
    }
  }
  
  // If not found in qualityMetrics, try getting from results
  if (plddtScores.length === 0 && results.plddt_scores) {
    plddtScores = results.plddt_scores as number[];
  }

  // Extract input sequence and parameters
  const inputSequence = results.query_sequence as string || '';
  const parameters = {
    msa_mode: results.msa_mode,
    num_relax: results.num_relax,
    pair_mode: results.pair_mode,
    num_models: results.num_models,
    max_recycles: results.max_recycles,
    num_recycles: results.num_recycles,
    stop_at_score: results.stop_at_score,
    template_mode: results.template_mode,
    rank_by: results.rank_by
  };
  
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        ColabFold Results
      </Typography>

      {/* Input Information Section */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Input Information
        </Typography>
        <Grid container spacing={2}>
          {/* Input Sequence */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Input Sequence
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                backgroundColor: 'grey.50',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all'
              }}
            >
              {inputSequence || 'No sequence provided'}
            </Paper>
          </Grid>

          {/* Parameters */}
          {Object.keys(parameters).length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Parameters
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(parameters).map(([key, value]) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 1.5,
                        backgroundColor: 'grey.50'
                      }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        {key}
                      </Typography>
                      <Typography variant="body1">
                        {String(value)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>
      
      {/* Quality Metrics visualization */}
      <QualityMetrics plddtScores={plddtScores} />
    </Box>
  );
}; 